import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiFetch } from "@/lib/api";

// ── Types (mirror backend/app/features/parent_companies/schemas.py) ──────────

export interface RosterChild {
  id: string;
  name: string;
  is_active: boolean | null;
  child_count: number;
}

export interface RosterParent {
  id: string;
  name: string;
  is_active: boolean | null;
  account_id: string | null;
  account_name: string | null;
  brand_name: string | null;
  city: string | null;
  state: string | null;
  children: RosterChild[];
  child_count: number;
  descendant_count: number;
  stacked: boolean;
  parent_ids: string[];
  parent_names: string[];
  pending_count: number;
  ai_status: "clear" | "flagged" | null;
  ai_reviewed_at: string | null;
}

export interface RosterOut {
  parents: RosterParent[];
  company_count: number;
  link_count: number;
  refreshed_at: string | null;
  cached: boolean;
}

export interface CompanyOption {
  id: string;
  name: string;
  is_active: boolean | null;
  account_name: string | null;
}

export type RequestAction = "add" | "remove" | "move";
export type RequestStatus = "pending" | "approved" | "rejected" | "cancelled";

export interface RelationshipRequest {
  id: string;
  action: RequestAction;
  parent_company_id: string;
  parent_name: string;
  child_company_id: string;
  child_name: string;
  new_parent_company_id: string | null;
  new_parent_name: string | null;
  reason: string;
  status: RequestStatus;
  requested_by: string | null;
  requested_by_email: string | null;
  reviewed_by: string | null;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  applied_at: string | null;
  apply_error: string | null;
  created_at: string;
  can_review: boolean;
  // Cinch has no write API for these links — approved requests carry a
  // prefilled Cinch Django-admin deep link for the human apply step.
  admin_link: string | null;
}

export interface RequestListOut {
  items: RelationshipRequest[];
  pending_total: number;
}

export interface ChangelogEntry {
  id: string;
  event: "request_created" | "approved" | "applied_verified" | "rejected" | "cancelled";
  action: RequestAction;
  parent_company_id: string;
  parent_name: string;
  child_company_id: string;
  child_name: string;
  new_parent_company_id: string | null;
  new_parent_name: string | null;
  detail: string | null;
  actor: string | null;
  actor_email: string | null;
  created_at: string;
}

export interface ChangelogOut {
  items: ChangelogEntry[];
  total: number;
}

export interface AIModelInfo {
  name: string;
  size_gb: number | null;
  is_default: boolean;
}

export interface AIModelsOut {
  models: AIModelInfo[];
  default_model: string | null;
  reachable: boolean;
}

export interface AIFinding {
  company_id: string | null;
  company_name: string | null;
  severity: "low" | "medium" | "high";
  concern: string;
  evidence: string | null;
}

export interface AIReviewOut {
  parent_company_id: string;
  parent_name: string;
  status: "clear" | "flagged";
  summary: string | null;
  findings: AIFinding[];
  model: string | null;
  reviewed_at: string | null;
  reviewed_by_email: string | null;
}

// ── Query keys ───────────────────────────────────────────────────────────────

const ROSTER_KEY = ["parent_companies", "roster"] as const;
const REQUESTS_KEY = ["parent_companies", "requests"] as const;
const CHANGELOG_KEY = ["parent_companies", "changelog"] as const;

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useRoster(refresh = false) {
  return useQuery({
    queryKey: [...ROSTER_KEY, refresh],
    queryFn: () =>
      apiFetch<RosterOut>(`/parent-companies/roster${refresh ? "?refresh=true" : ""}`),
    // The roster is a full Cinch sweep — don't hammer it on tab focus.
    staleTime: 5 * 60_000,
  });
}

export function useRefreshRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<RosterOut>("/parent-companies/roster?refresh=true"),
    onSuccess: (data) => {
      qc.setQueryData([...ROSTER_KEY, false], data);
      toast.success("Companies re-pulled from Cinch");
    },
    onError: (err) => toast.error(err.message),
  });
}

export function useCompanySearch(q: string) {
  return useQuery({
    queryKey: ["parent_companies", "company-search", q],
    queryFn: () =>
      apiFetch<{ items: CompanyOption[] }>(
        `/parent-companies/companies/search?q=${encodeURIComponent(q)}`,
      ),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  });
}

export function useRequests(status: "pending" | "all" = "all", companyId?: string) {
  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (companyId) params.set("company_id", companyId);
  const qs = params.toString();
  return useQuery({
    queryKey: [...REQUESTS_KEY, status, companyId ?? null],
    queryFn: () => apiFetch<RequestListOut>(`/parent-companies/requests${qs ? `?${qs}` : ""}`),
  });
}

export interface RequestCreateBody {
  action: RequestAction;
  parent_company_id: string;
  child_company_id: string;
  new_parent_company_id?: string;
  reason: string;
}

function useInvalidateWorkflow() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: REQUESTS_KEY });
    void qc.invalidateQueries({ queryKey: CHANGELOG_KEY });
    void qc.invalidateQueries({ queryKey: ROSTER_KEY });
  };
}

export function useCreateRequest() {
  const invalidate = useInvalidateWorkflow();
  return useMutation({
    mutationFn: (body: RequestCreateBody) =>
      apiFetch<RelationshipRequest>("/parent-companies/requests", { method: "POST", body }),
    onSuccess: invalidate,
  });
}

export function useApproveRequest() {
  const invalidate = useInvalidateWorkflow();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      apiFetch<RelationshipRequest>(`/parent-companies/requests/${id}/approve`, {
        method: "POST",
        body: { note: note ?? null },
      }),
    onSuccess: invalidate,
  });
}

export function useRejectRequest() {
  const invalidate = useInvalidateWorkflow();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      apiFetch<RelationshipRequest>(`/parent-companies/requests/${id}/reject`, {
        method: "POST",
        body: { note: note ?? null },
      }),
    onSuccess: invalidate,
  });
}

export function useCancelRequest() {
  const invalidate = useInvalidateWorkflow();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<RelationshipRequest>(`/parent-companies/requests/${id}/cancel`, {
        method: "POST",
      }),
    onSuccess: invalidate,
  });
}

export function useChangelog(companyId?: string) {
  const qs = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  return useQuery({
    queryKey: [...CHANGELOG_KEY, companyId ?? null],
    queryFn: () => apiFetch<ChangelogOut>(`/parent-companies/changelog${qs}`),
  });
}

export function useAIModels() {
  return useQuery({
    queryKey: ["parent_companies", "ai-models"],
    queryFn: () => apiFetch<AIModelsOut>("/parent-companies/ai-models"),
    staleTime: 5 * 60_000,
  });
}

const AI_REVIEWS_KEY = ["parent_companies", "ai-reviews"] as const;

export function useAIReviews() {
  return useQuery({
    queryKey: AI_REVIEWS_KEY,
    queryFn: () => apiFetch<{ items: AIReviewOut[] }>("/parent-companies/ai-reviews"),
  });
}

export function useRunAIReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, model }: { parentId: string; model?: string | null }) =>
      apiFetch<AIReviewOut>(`/parent-companies/ai-review/${parentId}`, {
        method: "POST",
        body: { model: model || null },
      }),
    onSuccess: (_data, { parentId }) => {
      void qc.invalidateQueries({ queryKey: ROSTER_KEY });
      void qc.invalidateQueries({ queryKey: AI_REVIEWS_KEY });
      void qc.invalidateQueries({
        queryKey: ["parent_companies", "ai-review", parentId],
      });
    },
  });
}

export function useAIReview(parentId: string) {
  return useQuery({
    queryKey: ["parent_companies", "ai-review", parentId],
    queryFn: () => apiFetch<AIReviewOut | null>(`/parent-companies/ai-review/${parentId}`),
    enabled: !!parentId,
  });
}
